import { forwardRef, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Mic, Paperclip, Square } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AttachMenu } from "@/components/files/AttachMenu";
import { AttachmentChips } from "@/components/files/AttachmentChips";
import { ModePicker } from "@/components/chat/ModePicker";
import { MODES } from "@/lib/research";
import { cn } from "@/lib/utils";

export const MAX_CHARS = 32000;

function SoonIcon({ icon: Icon, label, testId, tip }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex" data-testid={testId}>
          <button type="button" disabled aria-label={label} className="flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-full text-zinc-600">
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
          </button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="border border-white/10 bg-ink-800 text-zinc-200">{tip || `${label} — coming soon`}</TooltipContent>
    </Tooltip>
  );
}

export const Composer = forwardRef(function Composer(
  { value, onChange, onSubmit, onStop, busy = false, disabled = false, notice, sendKey = "enter", placeholder = "Ask anything…", autoFocus, variant = "chat", testIdPrefix = "chat-composer", uploads, research },
  ref,
) {
  const localRef = useRef(null);
  const inputRef = ref || localRef;
  const maxHeight = variant === "hero" ? 240 : 300;
  const tooLong = value.length > MAX_CHARS;
  const researching = Boolean(research?.on);
  const files = researching ? null : uploads;
  const attachmentsOk = !files || (files.ready && !files.busy);
  const canSend = value.trim().length > 0 && !busy && !disabled && !tooLong && attachmentsOk && !(researching && value.length > 4000);
  const attachNotice = files && !attachmentsOk ? (files.busy ? "Waiting for your files to finish uploading and reading…" : "Remove files ChatSleek can't read to send.") : researching && value.length > 4000 ? "Search questions can be up to 4,000 characters." : null;
  const mode = MODES[research?.mode || "chat"];

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, inputRef, maxHeight]);

  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    const mod = e.metaKey || e.ctrlKey;
    const wantsSend = sendKey === "enter" ? !e.shiftKey && !mod : mod;
    if (wantsSend) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          "border border-white/[0.11] bg-ink-850 px-2 py-2 transition-colors focus-within:border-white/[0.24]",
          variant === "hero" ? "rounded-[26px] sm:px-3 sm:py-2.5" : "rounded-[22px]",
        )}
        data-testid={`${testIdPrefix}-container`}
      >
        {files && <AttachmentChips items={files.items} onRemove={files.remove} className="px-1 pb-2 pt-1" />}
        <div className="flex items-end gap-1.5">
        {researching ? <SoonIcon icon={Paperclip} label="Attach files" tip={`Files aren't used in ${mode.label}`} testId={`${testIdPrefix}-attach-disabled`} /> : uploads ? <AttachMenu uploads={uploads} testIdPrefix={testIdPrefix} /> : <SoonIcon icon={Paperclip} label="Attach files" testId={`${testIdPrefix}-attach-button`} />}
        <textarea
          ref={inputRef}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={mode.placeholder || placeholder}
          autoFocus={autoFocus}
          aria-label="Message ChatSleek"
          data-testid={`${testIdPrefix}-input`}
          className="max-h-[300px] min-h-[36px] flex-1 resize-none bg-transparent px-1.5 py-[7px] text-[15px] leading-[22px] text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
        />
        {research && <ModePicker research={research} testIdPrefix={testIdPrefix} />}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to="/voice" aria-label="Talk with JARVIS" data-testid={`${testIdPrefix}-mic-button`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-white">
              <Mic className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </Link>
          </TooltipTrigger>
          <TooltipContent className="border border-white/10 bg-ink-800 text-zinc-200">Talk with JARVIS</TooltipContent>
        </Tooltip>
        {busy ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop generating"
            data-testid="chat-stop-button"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-ink-950 transition-transform hover:scale-[1.04] active:scale-95"
          >
            <Square className="h-3.5 w-3.5" fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => canSend && onSubmit()}
            disabled={!canSend}
            aria-label={mode.send}
            data-testid={`${testIdPrefix}-send-button`}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-ink-950 transition-[transform,opacity,background-color] hover:scale-[1.04] active:scale-95 disabled:scale-100 disabled:bg-white/15 disabled:text-zinc-500"
          >
            <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </button>
        )}
        </div>
      </div>
      {(notice || attachNotice || value.length > MAX_CHARS - 2000) && (
        <div className="mt-2 flex items-center justify-between px-3 text-[12px]">
          <span className="text-zinc-500" data-testid={`${testIdPrefix}-notice`}>{notice || attachNotice}</span>
          {value.length > MAX_CHARS - 2000 && (
            <span className={tooLong ? "text-rose-400" : "text-zinc-500"} data-testid={`${testIdPrefix}-char-count`}>
              {value.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
});
